import {ListItemActionBar, type ListItemActionBarProps} from '@github-ui/list-view/ListItemActionBar'
import type {CopilotCheckRunFailureContext} from '../../../page-data/payloads/status-checks'
import {publishOpenCopilotChat} from '@github-ui/copilot-chat/utils/copilot-chat-events'
import {CopilotChatIntents} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {parseJobUrlToReference} from '@github-ui/copilot-chat/utils/job-url-parser'
import {AgentIcon, ArrowRightIcon, CopilotIcon} from '@primer/octicons-react'
import {ActionList, Spinner} from '@primer/react'
import {useCallback, useState} from 'react'
import styles from './StatusCheckRowActionBar.module.css'
import {getFailedActionsJobPrompt} from '@github-ui/copilot-chat/utils/prompts'
import {useAnalytics} from '@github-ui/use-analytics'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {sendEvent} from '@github-ui/hydro-analytics'
import {referenceID} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {useCurrentRepository} from '@github-ui/current-repository'
import {useCreateFixWorkflowJob} from '@github-ui/fix-workflow-button/hooks/use-create-fix-workflow-job'

const EXPLAIN_ERROR_ACTION_ID = 'copilot-explain-error-action'
const FIX_WITH_COPILOT_ACTION_ID = 'copilot-fix-with-copilot-action'
export interface Props {
  copilotCheckRunFailureContext?: CopilotCheckRunFailureContext | null
  targetUrl?: string | null
  displayName?: string | null
}

function CopilotExplainErrorMenuItem({
  failureContext,
  targetUrl,
  displayName,
}: {
  failureContext: CopilotCheckRunFailureContext
  targetUrl?: string | null
  displayName?: string | null
}) {
  const onSelect = useCallback(() => {
    const errorRedirectEnabled = isFeatureEnabled('copilot_chat_action_logs_redirect')
    if (errorRedirectEnabled) {
      sendEvent('copilot.open_copilot_chat', {source: `element ${EXPLAIN_ERROR_ACTION_ID}`})
      const jobReference = targetUrl ? parseJobUrlToReference(targetUrl, true, displayName) : null
      const references = jobReference ? [jobReference] : []
      const prompt = getFailedActionsJobPrompt(undefined, true)
      copilotLocalStorage.setEntrypointMessage({message: prompt, references})
      copilotLocalStorage.setCurrentReferences(null, references)
      window.location.href = jobReference ? `/copilot?reference_id=${referenceID(jobReference)}` : '/copilot'
      return
    } else {
      publishOpenCopilotChat({
        id: EXPLAIN_ERROR_ACTION_ID,
        intent: CopilotChatIntents.actionsAgent,
        content: getFailedActionsJobPrompt(failureContext.jobId),
        references: [],
      })
    }
  }, [failureContext, targetUrl, displayName])

  return (
    <ActionList.Item onSelect={onSelect}>
      Explain error
      <ActionList.LeadingVisual>
        <CopilotIcon />
      </ActionList.LeadingVisual>
    </ActionList.Item>
  )
}

function ViewDetailsMenuItem({targetUrl}: {targetUrl: string}) {
  const {sendAnalyticsEvent} = useAnalytics()

  const handleClick = useCallback(() => {
    sendAnalyticsEvent('status_check_row_action_bar.view_details_click', 'VIEW_DETAILS_MENU_ITEM')
  }, [sendAnalyticsEvent])

  return (
    <ActionList.LinkItem href={targetUrl} onClick={handleClick}>
      View details
      <ActionList.LeadingVisual>
        <ArrowRightIcon />
      </ActionList.LeadingVisual>
    </ActionList.LinkItem>
  )
}

function CopilotFixWithCopilotMenuItem({
  failureContext,
  targetUrl,
  displayName,
  onLoadingChange,
}: {
  failureContext: CopilotCheckRunFailureContext
  targetUrl?: string | null
  displayName?: string | null
  onLoadingChange: (isLoading: boolean) => void
}) {
  const currentRepo = useCurrentRepository()

  // Parse repository info from URL
  const repoInfo = targetUrl ? parseJobUrlToReference(targetUrl, true, displayName) : null
  const owner = repoInfo?.repoOwner || currentRepo?.ownerLogin
  const repo = repoInfo?.repoName || currentRepo?.name
  // Use headRef from failure context (PR head branch) if available, otherwise fall back to default branch
  const branch = failureContext.headRef || currentRepo?.defaultBranch

  const {mutate: createFixJob} = useCreateFixWorkflowJob({
    owner,
    repo,
    onSuccess: data => {
      onLoadingChange(false)
      sendEvent('fix_workflow.job_created', {
        source: FIX_WITH_COPILOT_ACTION_ID,
      })
      // Redirect to the URL from the response, or fall back to the agents page
      window.location.href = data.redirectUrl || `/copilot/agents`
    },
    onError: error => {
      onLoadingChange(false)
      sendEvent('fix_workflow.job_creation_failed', {
        error: error.message,
        source: FIX_WITH_COPILOT_ACTION_ID,
      })
    },
  })

  const onSelect = useCallback(() => {
    onLoadingChange(true)
    sendEvent('fix_workflow.menu_item_clicked', {
      source: FIX_WITH_COPILOT_ACTION_ID,
    })

    const quotedDisplayName = displayName ? ` "${displayName}"` : ''
    const eventContent = `Fix the failing GitHub Actions job${quotedDisplayName}`
    const problemStatement = `${eventContent}
Analyze the Actions logs, identify the root cause of the failure, and implement a fix.
Job ID: ${failureContext.jobId}
${targetUrl ? `Job URL: ${targetUrl}` : ''}`

    createFixJob({
      problemStatement,
      baseRef: branch || '',
      eventType: 'fix_failed_workflow',
      eventUrl: targetUrl || '',
      eventContent,
    })
  }, [failureContext, targetUrl, displayName, createFixJob, branch, onLoadingChange])

  if (!repo || !owner || !branch) return null

  return (
    <ActionList.Item onSelect={onSelect}>
      Fix with agent
      <ActionList.LeadingVisual>
        <AgentIcon />
      </ActionList.LeadingVisual>
    </ActionList.Item>
  )
}

export function StatusCheckRowActionBar({copilotCheckRunFailureContext, targetUrl, displayName}: Props) {
  const [isCreatingFixTask, setIsCreatingFixTask] = useState(false)
  const staticMenuActions: ListItemActionBarProps['staticMenuActions'] = []
  const fixWorkflowEnabled = isFeatureEnabled('copilot_fix_failed_workflows')

  if (copilotCheckRunFailureContext != null) {
    staticMenuActions.push({
      key: 'copilot-explain-error',
      render: () => (
        <CopilotExplainErrorMenuItem
          failureContext={copilotCheckRunFailureContext}
          targetUrl={targetUrl}
          displayName={displayName}
        />
      ),
    })

    // Add "Fix with agent" option if feature is enabled
    if (fixWorkflowEnabled) {
      staticMenuActions.push({
        key: 'copilot-fix-with-copilot',
        render: () => (
          <CopilotFixWithCopilotMenuItem
            failureContext={copilotCheckRunFailureContext}
            targetUrl={targetUrl}
            displayName={displayName}
            onLoadingChange={setIsCreatingFixTask}
          />
        ),
      })
    }
  }

  if (targetUrl) {
    staticMenuActions.push({
      key: 'view-details',
      render: () => <ViewDetailsMenuItem targetUrl={targetUrl} />,
    })
  }

  // Show inline loading feedback when creating a fix task
  if (isCreatingFixTask) {
    return (
      <span className={styles.loadingIndicator} role="status" aria-live="polite" aria-atomic="true">
        <Spinner size="small" />
        <span className={styles.loadingText}>Creating task…</span>
      </span>
    )
  }

  // The label becomes "More actions" when the tooltip appears
  return (
    <ListItemActionBar label="actions" staticMenuActions={staticMenuActions} className={styles.statusCheckActionBar} />
  )
}
